import {createSlice} from '@reduxjs/toolkit'
// ----------------------------------------------------------------------

const initialState = {
  activeStep: 0,
  items: [],
  subTotal: 0,
  total: 0,
  discount: 0,
  discountLabel: '',
  tax: 0,
  totalItems: 0,
  paymentMethodSelected: 'cash',
  payment: { total: 0, methods: [] },
  customerSelected: {},
  invoiceSelected: {},
  printInvoice: true,
  voucher: null, // Selected voucher for current transaction
  voucherAmount: 0, // Amount applied from voucher
}

const stateSetTotal = (state, taxConfig, taxIncludedInItemPrice) => {
  state.items = state.items.filter((item) => item.quantity > 0)
  if (taxIncludedInItemPrice)
    state.items = state.items.map((item) => ({
        ...item,
        priceWithoutTax: taxIncludedInItemPrice
          ? (item.price - parseFloat(item.discount || '0')) / (1 + taxConfig / 100)
          : item.price - parseFloat(item.discount || '0'),
      }));
  state.totalItems = state.items.reduce((acc, item) => acc + parseInt(item.quantity, 10), 0);

  // Calculate subtotal and item discounts
  const itemsSubtotal = state.items.reduce(
    (acc, item) => {
      const itemTotal = parseFloat(item.priceWithoutTax || item.price) * parseInt(item.quantity, 10);
      return acc + itemTotal;
    },
    0
  );

  state.subTotal = parseFloat(itemsSubtotal.toFixed(2));

  state.tax = parseFloat(
    state.items
      .reduce(
        (acc, item) => {
          const itemPriceWithoutTax = parseFloat(item.priceWithoutTax || item.price);
          // const itemDiscount = item.discount || 0;
          // const discountedPrice = itemPriceWithoutTax - (itemDiscount / parseInt(item.quantity, 10));
          return acc + itemPriceWithoutTax * (taxConfig / 100) * parseInt(item.quantity, 10);
        },
        0
      )
      .toFixed(2)
  );

  // Calculate total including tax
  const totalWithTax = state.subTotal + state.tax;

  // Apply global discount to total including tax
  const globalDiscountAmount = state.discount > 0 ? Math.min(state.discount, totalWithTax) : 0;

  state.total = parseFloat((totalWithTax - globalDiscountAmount).toFixed(2));
};

const slice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    resetItems: (state) => {
      Object.entries(initialState).forEach(([key, value]) => {
        state[key] = value
      })
      // Ensure payment methods are also reset
      state.payment = { total: 0, methods: [] };
    },
    selectVoucher: (state, action) => {
      const { voucher, amount } = action.payload;
      // Extract voucher ID explicitly to ensure it's captured
      const voucherId = voucher?.id || voucher?._id;
      state.voucher = voucher;
      state.voucherAmount = amount || voucher?.remainingAmount || 0;
      // Add voucher as a payment method with voucherId
      if (!state.payment.methods) {
        state.payment.methods = [];
      }
      const existingIndex = state.payment.methods.findIndex(m => m.method === 'voucher' && m.voucherId === voucherId);
      if (existingIndex !== -1) {
        state.payment.methods[existingIndex].amount = state.voucherAmount;
        state.payment.methods[existingIndex].voucherId = voucherId;
      } else {
        state.payment.methods.push({ method: 'voucher', amount: state.voucherAmount, voucherId });
      }
      state.payment.total = parseFloat(state.payment.methods.reduce((sum, m) => sum + m.amount, 0).toFixed(2));
    },
    clearVoucher: (state) => {
      state.voucher = null;
      state.voucherAmount = 0;
      // Remove voucher from payment methods
      if (state.payment.methods) {
        state.payment.methods = state.payment.methods.filter(m => m.method !== 'voucher');
        state.payment.total = parseFloat(state.payment.methods.reduce((sum, m) => sum + m.amount, 0).toFixed(2));
      }
    },
    updateVoucherAmount: (state, action) => {
      state.voucherAmount = action.payload;
      // Extract voucher ID from stored voucher
      const voucherId = state.voucher?.id || state.voucher?._id;
      // Update voucher payment method amount and voucherId
      if (state.payment.methods) {
        const existingIndex = state.payment.methods.findIndex(m => m.method === 'voucher');
        if (existingIndex !== -1) {
          state.payment.methods[existingIndex].amount = action.payload;
          state.payment.methods[existingIndex].voucherId = voucherId;
          state.payment.total = parseFloat(state.payment.methods.reduce((sum, m) => sum + m.amount, 0).toFixed(2));
        }
      }
    },
    selectInvoice: (state, action) => {
      const {payload} = action
      if(payload) Object.entries(payload).forEach(([key, value]) => { state[key] = value })
      if (payload.customer) {
        // Map voucher fields from server format to frontend format
        const customer = payload.customer?.vouchers ? {
          ...payload.customer,
          vouchers: payload.customer.vouchers.map(v => ({
            ...v,
            id: v.id || v._id,
            totalAmount: v.totalAmount || v.initialAmount || 0,
            remainingAmount: v.remainingAmount || v.currentAmount || 0,
            usedAmount: v.usedAmount || (v.initialAmount - v.currentAmount) || 0,
            customerId: v.customer?.id || v.customer,
            createdAt: v.createdAt || v.created_at,
            expiresAt: v.expiresAt || v.expiryDate,
          }))
        } : payload.customer;
        state.customerSelected = customer;
      }
      state.invoiceSelected = payload
    },
    selectCustomer: (state, action) => {
      const {payload} = action
      // Map voucher fields from server format to frontend format
      const customer = payload?.vouchers ? {
        ...payload,
        vouchers: payload.vouchers.map(v => ({
          ...v,
          id: v.id || v._id,
          totalAmount: v.totalAmount || v.initialAmount || 0,
          remainingAmount: v.remainingAmount || v.currentAmount || 0,
          usedAmount: v.usedAmount || (v.initialAmount - v.currentAmount) || 0,
          customerId: v.customer?.id || v.customer,
          createdAt: v.createdAt || v.created_at,
          expiresAt: v.expiresAt || v.expiryDate,
        }))
      } : payload;
      state.customerSelected = customer
    },
    updateItems: (state, action) => {
      const {
        payload: { data, taxConfig, taxIncludedInItemPrice },
      } = action;

      // Créer une nouvelle copie des items avec les mises à jour
      state.items = state.items.map(item => {
        const updatedProduct = data.find(p => p?.id === item.id);
        if (!updatedProduct) return item;

        // Créer une copie de l'item avec les propriétés mises à jour
        return {
          ...item,
          available: updatedProduct.available ? parseInt(updatedProduct.available, 10) : item.available,
          quantity: updatedProduct.quantity
            ? Math.min(parseInt(updatedProduct.quantity, 10), item.available)
            : item.quantity,
          price: updatedProduct.price ?? item.price
        };
      });

      stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
    },
    increaseQuantity: (state, action) => {
      const {
        payload: { item, taxConfig, taxIncludedInItemPrice },
      } = action;
      const itemIndex = state.items.findIndex(itm => itm.id === item?.id);

      if (itemIndex !== -1) {
        // Si l'article existe, créer un nouveau tableau avec l'article mis à jour
        if (state.items[itemIndex].available > state.items[itemIndex].quantity) {
          state.items = state.items.map((itm, idx) =>
            idx === itemIndex
              ? {...itm, quantity: itm.quantity + 1}
              : itm
          );
        }
      } else if (item?.id) {
        // Si l'article n'existe pas, ajouter le nouvel article
        state.items = [...state.items, { ...item, quantity: 1 }];
      }

      stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
    },
    decreaseQuantity: (state, action) => {
      const {
        payload: { item, taxConfig, taxIncludedInItemPrice },
      } = action;
      const itemIndex = state.items.findIndex(itm => itm.id === item?.id);

      if (itemIndex !== -1) {
        if (state.items[itemIndex].quantity <= 1) {
          // Si la quantité est 1 ou moins, supprimer l'article du panier
          state.items = state.items.filter((_, idx) => idx !== itemIndex);
        } else {
          // Sinon, décrémenter la quantité
          state.items = state.items.map((itm, idx) =>
            idx === itemIndex
              ? {...itm, quantity: itm.quantity - 1}
              : itm
          );
        }
      }

      stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
    },
    updatePrice: (state, action) => {
      const {
        payload: { item, price, taxConfig, taxIncludedInItemPrice },
      } = action;

      state.items = state.items.map((itm) =>
        itm.id === item?.id ? {...itm, price} : itm
      );

      stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
    },
    updatePayment: (state, action) => {
      const { method, amount, remove } = action.payload;

      if (!state.payment.methods) {
        state.payment.methods = [];
      }

      if (remove) {
        state.payment.methods = state.payment.methods.filter(m => m.method !== method);
      } else {
        const existingIndex = state.payment.methods.findIndex(m => m.method === method);
        if (existingIndex !== -1) {
          state.payment.methods[existingIndex].amount = amount;
        } else {
          state.payment.methods.push({ method, amount });
        }
      }

      state.payment.total = parseFloat(state.payment.methods.reduce((sum, m) => sum + m.amount, 0).toFixed(2));
    },
    nextStep: (state) => {
      console.log("next step customer selected", state.customerSelected);
      if (state.activeStep < 2) state.activeStep += 1
    },
    backStep: (state) => {
      if (state.activeStep > 0) state.activeStep -= 1
    },
    firstStep: (state) => {
      state.activeStep = 0
    },
    updatePrintInvoice: (state, action) => {
      state.printInvoice = action.payload
    },
    updatePaymentMethod: (state, action) => {
      state.paymentMethodSelected = action.payload
    },
    applyDiscount: (state, action) => {
      const { amount, label } = action.payload;
      // Calculate total including tax for discount calculation
      const totalWithTax = state.subTotal + state.tax;
      const globalDiscountAmount = amount > 0 ? Math.min(amount, totalWithTax) : 0;

      state.discount = parseFloat(globalDiscountAmount.toFixed(2));
      state.discountLabel = label || '';
      // Recalculate total with global discount
      state.total = parseFloat((totalWithTax - globalDiscountAmount).toFixed(2));
    },
    clearDiscount: (state) => {
      state.discount = 0;
      state.discountLabel = '';
      // Recalculate total without global discount
      const totalWithTax = state.subTotal + state.tax;
      state.total = parseFloat(totalWithTax.toFixed(2));
    },
    applyItemDiscount: (state, action) => {
      const { itemId, amount, label, taxConfig, taxIncludedInItemPrice } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === itemId);

      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
        const maxDiscount = parseFloat(item.price) * item.quantity;
        const discountAmount = Math.min(amount, maxDiscount);

        state.items[itemIndex] = {
          ...item,
          discount: discountAmount,
          discountLabel: label || '',
        };

        // Recalculate totals
        stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
      }
    },
    clearItemDiscount: (state, action) => {
      const { itemId, taxConfig, taxIncludedInItemPrice } = action.payload;
      const itemIndex = state.items.findIndex(item => item.id === itemId);

      if (itemIndex !== -1) {
        state.items[itemIndex] = {
          ...state.items[itemIndex],
          discount: 0,
          discountLabel: ''
        };

        // Recalculate totals
        stateSetTotal(state, taxConfig, taxIncludedInItemPrice);
      }
    }
  }
})

// Reducer
export default slice.reducer

// Actions
export const {
  resetItems,
  selectInvoice,
  selectCustomer,
  updateItems,
  increaseQuantity,
  decreaseQuantity,
  updatePrice,
  nextStep,
  backStep,
  firstStep,
  updatePayment,
  updatePrintInvoice,
  updatePaymentMethod,
  selectVoucher,
  clearVoucher,
  updateVoucherAmount,
  applyDiscount,
  clearDiscount,
  applyItemDiscount,
  clearItemDiscount,
} = slice.actions;
